// Phase 5 (full search) thunks — status polling, rule-out drilldowns,
// candidate paging and rescue. Split out of polarisThunks to keep that file
// within the repo's per-file size budget; re-exported from there.
import {createAction, createAsyncThunk} from "@reduxjs/toolkit";
import polarisApi from "api/polarisApi";
import type {TCandidateSort} from "api/polarisApi/types";
import type {TRootState} from "types";
import {readStoredCandidateSort, storeCandidateSort} from "./candidateSort";
import {ensureOk, searchIdOf} from "./thunkUtils";

const POLL_INTERVAL_MS = 3000;

// Handle for the in-flight poll timer so it can be cancelled outright when the
// page unmounts, rather than relying on the next tick to no-op.
let pollTimer: ReturnType<typeof setTimeout> | null = null;

/** Cancels any pending search-status poll. Call on unmount. */
export const clearSearchPollTimer = () => {
	if (pollTimer) {
		clearTimeout(pollTimer);
		pollTimer = null;
	}
};

export const loadRuleouts = createAsyncThunk("polaris/loadRuleouts", async (_: void, {getState}) =>
	ensureOk(await polarisApi.getRuleouts(searchIdOf(getState)), "Could not load rule-outs"),
);

// The order chosen in this visit, else the one this browser remembers.
const candidateSortOf = (getState: () => unknown): TCandidateSort =>
	(getState() as TRootState).polaris.search_run?.sort ?? readStoredCandidateSort();

// A page of the ranked list, in the chosen order (or `sort` when given). The
// payload carries the order it was read in, so the reducer can drop a page
// that lands after the viewer changed the order.
export const loadCandidates = createAsyncThunk(
	"polaris/loadCandidates",
	async (
		{sort: requested, ...pagination}: {limit: number; offset: number; sort?: TCandidateSort},
		{getState},
	) => {
		const sort = requested ?? candidateSortOf(getState);
		const list = ensureOk(
			await polarisApi.getCandidates(searchIdOf(getState), pagination, sort),
			"Could not load candidates",
		);

		return {...list, sort};
	},
);

export const candidateSortChanged = createAction<TCandidateSort>("polaris/candidateSortChanged");

// "Sort by": remembered in this browser, then the list is read again from the
// first page in the new order (the server orders the whole list). If that read
// fails the list keeps its old order, so the choice goes back to match it.
export const changeCandidateSort = createAsyncThunk(
	"polaris/changeCandidateSort",
	async (sort: TCandidateSort, {dispatch, getState}) => {
		const previous = candidateSortOf(getState);

		storeCandidateSort(sort);
		dispatch(candidateSortChanged(sort));

		const read = await dispatch(loadCandidates({limit: 15, offset: 0, sort}));

		if (loadCandidates.rejected.match(read)) {
			storeCandidateSort(previous);
			dispatch(candidateSortChanged(previous));
		}
	},
);

export const pollSearchStatus = createAsyncThunk(
	"polaris/pollSearchStatus",
	async (_: void, {getState, dispatch}) => {
		const status = ensureOk(
			await polarisApi.getSearchStatus(searchIdOf(getState)),
			"Could not read the search status",
		);

		const done = status.stages.every((stage) => stage.status === "done");

		if (done) {
			clearSearchPollTimer();
			await Promise.all([
				dispatch(loadRuleouts()),
				dispatch(loadCandidates({limit: 15, offset: 0})),
			]);
		} else {
			clearSearchPollTimer();
			pollTimer = setTimeout(() => {
				// Stop silently if the page unmounted (polling flag cleared)
				const stillPolling = (getState() as TRootState).polaris.search_run.polling;

				if (stillPolling) dispatch(pollSearchStatus());
			}, POLL_INTERVAL_MS);
		}

		return status;
	},
);

export const loadRuleoutCandidates = createAsyncThunk(
	"polaris/loadRuleoutCandidates",
	async (arg: {ruleoutId: string; limit?: number; offset?: number}, {getState}) =>
		ensureOk(
			await polarisApi.getRuleoutCandidates(searchIdOf(getState), arg.ruleoutId, {
				limit: arg.limit ?? 20,
				offset: arg.offset ?? 0,
			}),
			"Could not load the dropped candidates",
		),
);

export const rescueCandidate = createAsyncThunk(
	"polaris/rescueCandidate",
	async (arg: {ruleoutId: string; candidateRef: string}, {getState, dispatch}) => {
		const result = ensureOk(
			await polarisApi.postRescue(searchIdOf(getState), arg.ruleoutId, arg.candidateRef),
			"Could not rescue the candidate",
		);

		// Ranked list and breakdown both changed
		await Promise.all([
			dispatch(loadRuleouts()),
			dispatch(loadCandidates({limit: 15, offset: 0})),
		]);

		return {...result, ruleout_id: arg.ruleoutId};
	},
);
